import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, Calculator, Calendar, DollarSign, FileText } from 'lucide-react';

const UF_TO_CLP = 40000;

const formatCLP = (value) => {
  if (!value && value !== 0) return '-';
  return `$${Math.round(value).toLocaleString('es-CL')}`;
};

export const CuotasCalculator = ({
  precioUf,
  numCuotas,
  onCuotasChange,
  billingStartMode = 'immediate',
  billingStartDate = '',
  // Honorario a resultado
  honorarioResultadoTexto = '',
  aiSlot = null,
  onHonorarioResultadoChange,
  // Reparto
  repartoMode = 'iguales',
  onRepartoModeChange,
  primeraCuotaUf = '',
  onPrimeraCuotaChange,
  // Fecha
  fechaPrimeraCuota = '',
  onFechaPrimeraCuotaChange,
  disabled = false,
}) => {
  const [cuotas, setCuotas] = useState([]);
  const [editedCuotas, setEditedCuotas] = useState({});

  const precio = parseFloat(precioUf) || 0;
  const nCuotas = parseInt(numCuotas) || 0;
  const primera = parseFloat(primeraCuotaUf) || 0;

  // Generate cuotas when inputs change
  useEffect(() => {
    if (precio <= 0 || nCuotas <= 0) {
      setCuotas([]);
      return;
    }

    const nuevasCuotas = [];
    for (let i = 0; i < nCuotas; i++) {
      let montoUf;
      if (repartoMode === 'inicial_saldo' && primera > 0 && nCuotas > 1) {
        montoUf = i === 0 ? primera : (precio - primera) / (nCuotas - 1);
      } else {
        montoUf = precio / nCuotas;
      }

      let fecha = '';
      if (billingStartMode === 'after_related_psp') {
        fecha = '';
      } else if (fechaPrimeraCuota) {
        const base = new Date(fechaPrimeraCuota + 'T00:00:00');
        const newDate = new Date(base);
        newDate.setDate(newDate.getDate() + 30 * i);
        fecha = newDate.toISOString().split('T')[0];
      }

      const edited = editedCuotas[i];
      nuevasCuotas.push({
        numero: i + 1,
        monto_uf: edited?.monto_uf !== undefined ? edited.monto_uf : parseFloat(montoUf.toFixed(2)),
        fecha_vencimiento: edited?.fecha_vencimiento !== undefined ? edited.fecha_vencimiento : fecha,
      });
    }

    setCuotas(nuevasCuotas);
  }, [precio, nCuotas, repartoMode, primera, fechaPrimeraCuota, billingStartMode, editedCuotas]);

  useEffect(() => {
    if (cuotas.length > 0 && onCuotasChange) {
      onCuotasChange(cuotas);
    }
  }, [cuotas]);

  useEffect(() => {
    setEditedCuotas({});
  }, [nCuotas, repartoMode]);

  const handleCuotaAmountChange = useCallback((index, value) => {
    const numVal = parseFloat(value) || 0;
    setEditedCuotas(prev => ({
      ...prev,
      [index]: { ...prev[index], monto_uf: numVal }
    }));
  }, []);

  const handleCuotaDateChange = useCallback((index, value) => {
    setEditedCuotas(prev => ({
      ...prev,
      [index]: { ...prev[index], fecha_vencimiento: value }
    }));
    if (index === 0 && value) {
      const base = new Date(value + 'T00:00:00');
      const newEdits = { ...editedCuotas, 0: { ...editedCuotas[0], fecha_vencimiento: value } };
      for (let i = 1; i < nCuotas; i++) {
        if (!editedCuotas[i]?.fecha_vencimiento) {
          const newDate = new Date(base);
          newDate.setDate(newDate.getDate() + 30 * i);
          newEdits[i] = { ...newEdits[i], fecha_vencimiento: newDate.toISOString().split('T')[0] };
        }
      }
      setEditedCuotas(newEdits);
      if (onFechaPrimeraCuotaChange) onFechaPrimeraCuotaChange(value);
    }
  }, [editedCuotas, nCuotas, onFechaPrimeraCuotaChange]);

  const totalCuotas = useMemo(() => 
    cuotas.reduce((sum, c) => sum + (c.monto_uf || 0), 0),
    [cuotas]
  );
  const diferencia = useMemo(() => Math.abs(totalCuotas - precio), [totalCuotas, precio]);
  const hayDesbalance = precio > 0 && nCuotas > 0 && diferencia > 0.02;

  if (precio <= 0 || nCuotas <= 0) return null;

  return (
    <div className="space-y-4" data-testid="cuotas-calculator">
      {/* Modo de Reparto */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Label className="text-sm text-slate-700 mb-1 block flex items-center gap-1.5">
            <Calculator className="w-3.5 h-3.5" />
            Modo de Reparto
          </Label>
          <Select value={repartoMode} onValueChange={onRepartoModeChange} disabled={disabled}>
            <SelectTrigger data-testid="reparto-mode-select" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="iguales">Cuotas Iguales</SelectItem>
              <SelectItem value="inicial_saldo">Cuota Inicial + Saldo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {repartoMode === 'inicial_saldo' && (
          <div className="flex-1">
            <Label className="text-sm text-slate-700 mb-1 block">Primera Cuota (UF)</Label>
            <Input
              type="number"
              step="0.01"
              value={primeraCuotaUf}
              onChange={(e) => onPrimeraCuotaChange(e.target.value)}
              placeholder="Monto primera cuota"
              className="h-9"
              disabled={disabled}
              data-testid="primera-cuota-uf-input"
            />
          </div>
        )}
        <div className="flex-1">
          <Label className="text-sm text-slate-700 mb-1 block flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            Fecha Cuota 1
          </Label>
          <Input
            type="date"
            value={fechaPrimeraCuota}
            onChange={(e) => {
              if (onFechaPrimeraCuotaChange) onFechaPrimeraCuotaChange(e.target.value);
              handleCuotaDateChange(0, e.target.value);
            }}
            className="h-9"
            disabled={disabled || billingStartMode === 'after_related_psp'}
            data-testid="fecha-primera-cuota-input"
          />
        </div>
      </div>

      {/* Nota UF referencial */}
      <div className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2">
        <p className="text-xs text-sky-700">
          <DollarSign className="w-3 h-3 inline mr-1" />
          <strong>Valor referencial:</strong> 1 UF = {formatCLP(UF_TO_CLP)} CLP. 
          El monto definitivo se calcula al valor de la UF de la fecha de facturación.
        </p>
      </div>

      {/* Tabla de Cuotas Editable */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="cuotas-table">
          <thead>
            <tr className="bg-slate-800 text-white">
              <th className="px-3 py-2 text-left font-medium w-16">N</th>
              <th className="px-3 py-2 text-left font-medium">Monto (UF)</th>
              <th className="px-3 py-2 text-left font-medium">Ref. CLP</th>
              <th className="px-3 py-2 text-left font-medium">Fecha Vencimiento</th>
            </tr>
          </thead>
          <tbody>
            {cuotas.map((cuota, idx) => (
              <tr key={idx} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                <td className="px-3 py-1.5 text-slate-600 font-medium">{cuota.numero}</td>
                <td className="px-3 py-1.5">
                  <Input
                    type="number"
                    step="0.01"
                    value={cuota.monto_uf}
                    onChange={(e) => handleCuotaAmountChange(idx, e.target.value)}
                    className="h-7 text-sm w-28"
                    disabled={disabled}
                    data-testid={`cuota-monto-${idx}`}
                  />
                </td>
                <td className="px-3 py-1.5 text-slate-500 text-xs">
                  {formatCLP(cuota.monto_uf * UF_TO_CLP)}
                </td>
                <td className="px-3 py-1.5">
                  {billingStartMode === 'after_related_psp' ? (
                    <span className="text-xs text-blue-600 italic">
                      {idx === 0 ? 'Al activarse la cobranza' : `Mes ${idx + 1} desde activación`}
                    </span>
                  ) : (
                    <Input
                      type="date"
                      value={cuota.fecha_vencimiento || ''}
                      onChange={(e) => handleCuotaDateChange(idx, e.target.value)}
                      className="h-7 text-sm w-40"
                      disabled={disabled}
                      data-testid={`cuota-fecha-${idx}`}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 font-semibold">
              <td className="px-3 py-2 text-slate-700">Total</td>
              <td className="px-3 py-2 text-slate-900">{totalCuotas.toFixed(2)} UF</td>
              <td className="px-3 py-2 text-slate-500 text-xs">{formatCLP(totalCuotas * UF_TO_CLP)}</td>
              <td className="px-3 py-2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Alerta de desbalance */}
      {hayDesbalance && (
        <div className="bg-red-50 border border-red-300 rounded-md px-3 py-2 flex items-center gap-2" data-testid="cuotas-warning">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
          <p className="text-sm text-red-700">
            El total de cuotas (<strong>{totalCuotas.toFixed(2)} UF</strong>) no coincide con el honorario pactado (<strong>{precio.toFixed(2)} UF</strong>).
            Diferencia: {diferencia.toFixed(2)} UF.
          </p>
        </div>
      )}

      {/* Honorario a Resultado - Texto libre */}
      <div className="border-t border-slate-200 pt-4 mt-2">
        <Label className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-emerald-600" />
          Honorarios a Resultado
        </Label>
        <p className="text-xs text-slate-500 mb-2">
          Si aplica, describe las condiciones del fee a resultado. Este texto aparecerá en el PDF de la PSP y en el CPS.
        </p>
        <Textarea
          value={honorarioResultadoTexto}
          onChange={(e) => onHonorarioResultadoChange && onHonorarioResultadoChange(e.target.value)}
          placeholder='Ej: "10% del ahorro efectivo del CEEC" o "15 UF al obtener sentencia favorable"'
          className="min-h-[60px] text-sm"
          disabled={disabled}
          data-testid="honorario-resultado-textarea"
        />
        {aiSlot}
      </div>
    </div>
  );
};

export default CuotasCalculator;
